import {
  ActivityChangedDocument,
  ActivityDatesDocument,
  ActivityCoreFragmentDoc,
  IssueNotesDataDocument,
} from "#app/lib/data";
import {
  defineSubscription,
  type AnySubscriptionRequest,
} from "#app/lib/subscriptions/subscription-provider";
import {
  addActivityToCache,
  removeActivityFromCache,
} from "./note-cache-utils";

// ---------------------------------------------------------------------------
// Issue activity-feed realtime — registered by useActivityViewData while an
// issue's activity tab is mounted. Events are UPSERTS plus a deleted flag
// (activities merge into 5-minute ongoing rows server-side and can delete
// when changes revert), so the handler writes the normalized entity and only
// inserts an edge when the row isn't already listed.
// ---------------------------------------------------------------------------

export function activitySubscriptions(
  issueId: string,
): AnySubscriptionRequest[] {
  return [
    defineSubscription({
      document: ActivityChangedDocument,
      variables: { issueId },
      onData: (client, data) => {
        const event = data.activityChanged;
        if (!event) return;
        const { activity, deleted } = event;

        const cacheId = client.cache.identify({
          __typename: "Activity",
          activityId: activity.activityId,
        });

        if (deleted) {
          removeActivityFromCache(client.cache, issueId, activity.activityId);
          // A deleted row may have been a date's only activity.
          refreshActivityDates(client, issueId);
          return;
        }
        // identify() is undefined only for an object missing its key fields —
        // impossible for a server payload, but the narrowing is required.
        if (cacheId === undefined) return;

        // Upsert the normalized row (merge events update a rendered row in
        // place), then list it if it's new.
        const isNew =
          client.cache.readFragment({
            id: cacheId,
            fragment: ActivityCoreFragmentDoc,
            fragmentName: "ActivityCore",
          }) === null;
        client.cache.writeFragment({
          id: cacheId,
          fragment: ActivityCoreFragmentDoc,
          fragmentName: "ActivityCore",
          data: activity,
        });
        addActivityToCache(client.cache, issueId, activity);
        // Only a NEW row can introduce a day-jump date; merges reuse their
        // row's existing date.
        if (isNew) refreshActivityDates(client, issueId);

        // Note activities render their body from the separate notes query
        // (apiv2) — refetch it so a note arriving over this subscription has
        // its content, replies, and reactions available to the feed row.
        if (activity.subject === "note") {
          void client.refetchQueries({ include: [IssueNotesDataDocument] });
        }
      },
    }),
  ];
}

/**
 * Refresh the day-jump date chips. Writes the same root cache field the
 * bundled ActivityViewData query reads (identical args), so the activity
 * view's cache-and-network watch re-emits with the new dates — no extra
 * state plumbing. Timezone math is server-side, hence a refetch rather than
 * deriving the date locally.
 */
function refreshActivityDates(
  client: Parameters<AnySubscriptionRequest["onData"]>[0],
  issueId: string,
): void {
  void client
    .query({
      query: ActivityDatesDocument,
      variables: {
        issueId,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      fetchPolicy: "network-only",
    })
    .catch(() => {});
}
